'use client';

// A single confirm-before-acting dialog. The cancel/keep pair was previously
// hand-rolled at each call site, which is how two dialogs for the same kind of
// decision end up worded and laid out differently.
//
// The safe choice stays on the left and the committing choice on the right, so
// the destructive action is never where a user's muscle memory expects "no".
//
// Wording (DESIGN.md, Actions): a destructive confirm backs out with "Keep
// [thing]" and commits in red; every other confirm backs out with "Cancel".
//
// An onConfirm that returns a promise keeps the dialog open with the confirm
// spinning until it settles, then closes it; a rejection leaves it open (the
// caller has already said what went wrong). A plain one closes at once.

import * as React from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

export interface ConfirmDialogProps {
  /** The control that opens the dialog. Rendered as-is via asChild. Leave it out when `open` drives the dialog. */
  trigger?: React.ReactNode;
  /** Controlled open state, for a dialog opened from a menu or by code. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  /** Anything the decision needs beyond the description (a list, a field). */
  children?: React.ReactNode;
  /** Label for the committing action. */
  confirmLabel: string;
  /** Label for the safe action. Phrase it as the outcome ("Keep tier") on a destructive confirm. */
  cancelLabel?: string;
  onConfirm: () => void | Promise<unknown>;
  variant?: 'destructive' | 'default';
  /** Holds the confirm (and the dialog) busy while the caller's own work runs. */
  pending?: boolean;
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  trigger,
  open: openProp,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  variant = 'destructive',
  pending = false,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const [openState, setOpenState] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const busy = pending || running;

  const setOpen = (next: boolean) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  };

  const confirm = async () => {
    const result = onConfirm();
    if (!(result instanceof Promise)) {
      setOpen(false);
      return;
    }
    setRunning(true);
    try {
      await result;
      setOpen(false);
    } catch {
      // The caller reported the failure; stay open so the choice can be retried.
    } finally {
      setRunning(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!busy) setOpen(next); }}>
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{cancelLabel}</AlertDialogCancel>
          <Button
            type="button"
            variant={variant}
            loading={busy}
            disabled={confirmDisabled}
            onClick={() => void confirm()}
          >
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
